// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { findAppConfig, updateAppConfig } from '@api/portalServices';
import type { AppConfigGetResponse, AppConfigUpdateRequest } from '@api/types';
import { useSession } from '@common/authorization/context/Session';
import type { ApiError } from '@errorHandler/errors';
import { QueryKey } from '@portal/constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const EPIC_HYPERDRIVE = 'epic-hyperdrive';

/**
 * Fetches application configuration for the given `applicationId`. Reads
 * `tenantId` from the session context and threads it into the BFF call so the
 * request carries tenant scope on hosts where the URL does not. Callers pass
 * `enabled: false` to defer the fetch (e.g. modal-only); the hook additionally
 * defers until `tenantId` is available so an empty tenant never reaches the BFF.
 */
export const useAppConfigQuery = (applicationId: string, opts?: { enabled?: boolean }) => {
  const { tenantId } = useSession();
  return useQuery<AppConfigGetResponse, ApiError>({
    queryKey: [QueryKey.APP_CONFIG, applicationId, tenantId],
    queryFn: () => findAppConfig(applicationId, tenantId || ''),
    enabled: (opts?.enabled ?? true) && Boolean(tenantId),
    retry: false,
    staleTime: 0,
  });
};

/**
 * Submits an application configuration update. On success the matching
 * `APP_CONFIG` cache entry and the `APP_INTEGRATION` entry for
 * `epic-hyperdrive` are invalidated so the page reflects the saved state on
 * the next open without a hard reload. Reads `tenantId` from the session
 * context and threads it into the BFF call.
 */
export const useAppConfigMutation = (applicationId: string) => {
  const queryClient = useQueryClient();
  const { tenantId } = useSession();

  return useMutation<void, ApiError, AppConfigUpdateRequest>({
    mutationFn: async (data: AppConfigUpdateRequest) => {
      await updateAppConfig(data, tenantId || '');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.APP_CONFIG, applicationId] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.APP_INTEGRATION, EPIC_HYPERDRIVE] });
    },
    retry: false,
  });
};
